import type { RevitElement, RevitFamily } from '../api/revit/types';
import { startQuickFillApplyGrpc } from '../api/grpc/quickFillApplyClient';

const ParameterName = '#PCF_SKEY';
const ReadOnlyParameterName = '#PCF_OBJECT_TYPE';

type QuickFillApply = typeof startQuickFillApplyGrpc;
type QuickFillFamilyTypeUpdate = Parameters<QuickFillApply>[0][number];
type QuickFillElementUpdate = Parameters<QuickFillApply>[1][number];

interface RevitSnapshotLike {
  elements: RevitElement[];
  families: RevitFamily[];
}

interface QuickFillApplyEvidence {
  familyTypeUpdates: QuickFillFamilyTypeUpdate[];
  elementUpdates: QuickFillElementUpdate[];
}

declare global {
  interface Window {
    __PIPEEX_E2E_QUICK_FILL_APPLY__?: QuickFillApplyEvidence;
  }
}

function isMixedScopeFixtureActive(search: string): boolean {
  if (process.env.REACT_APP_PIPEEX_E2E_BYPASS !== '1') {
    return false;
  }

  const params = new URLSearchParams(search);
  return params.get('e2e') === '1' && params.get('e2eQuickFillMixedScopes') === '1';
}

function buildFamilies(): RevitFamily[] {
  return [{
    id: 'e2e-mixed-scope-family',
    name: 'E2E Mixed Scope',
    category: 'PipeFittings',
    types: [
      {
        id: 'e2e-instance-bound',
        name: 'Instance Bound',
        parameterValues: [{
          parameterId: 'e2e-instance-type-readonly',
          parameterName: ReadOnlyParameterName,
          value: 'TYPERO',
          isReadOnly: true,
          isInstance: false,
        }],
      },
      {
        id: 'e2e-type-bound',
        name: 'Type Bound',
        parameterValues: [{
          parameterId: 'e2e-type-parameter',
          parameterName: ParameterName,
          value: 'TYPE-OLD',
          isReadOnly: false,
          isInstance: false,
        }, {
          parameterId: 'e2e-type-readonly',
          parameterName: ReadOnlyParameterName,
          value: 'TYPEONLY',
          isReadOnly: true,
          isInstance: false,
        }],
      },
    ],
    parameters: [],
  }];
}

function buildElements(): RevitElement[] {
  return [
    {
      id: 'e2e-instance-element',
      name: 'Instance Bound Component',
      type: 'Family',
      category: 'PipeFittings',
      familyName: 'E2E Mixed Scope',
      typeName: 'Instance Bound',
      parameters: [],
      parameterValues: [{
        parameterId: 'e2e-instance-parameter',
        parameterName: ParameterName,
        value: 'INST-OLD',
        isReadOnly: false,
        isInstance: true,
      }, {
        parameterId: 'e2e-instance-readonly',
        parameterName: ReadOnlyParameterName,
        value: 'INSTRO',
        isReadOnly: true,
        isInstance: true,
      }, {
        parameterId: 'e2e-instance-type-readonly',
        parameterName: ReadOnlyParameterName,
        value: 'TYPERO',
        isReadOnly: true,
        isInstance: false,
      }],
    },
    {
      id: 'e2e-type-element',
      name: 'Type Bound Component',
      type: 'Family',
      category: 'PipeFittings',
      familyName: 'E2E Mixed Scope',
      typeName: 'Type Bound',
      parameters: [],
      parameterValues: [{
        parameterId: 'e2e-type-parameter',
        parameterName: ParameterName,
        value: 'TYPE-OLD',
        isReadOnly: false,
        isInstance: false,
      }, {
        parameterId: 'e2e-type-readonly',
        parameterName: ReadOnlyParameterName,
        value: 'TYPEONLY',
        isReadOnly: true,
        isInstance: false,
      }],
    },
  ];
}

export function resolveE2eQuickFillMixedScopeSnapshot<T extends RevitSnapshotLike>(
  snapshot: T,
  search: string = window.location.search
): T {
  if (!isMixedScopeFixtureActive(search)) {
    return snapshot;
  }

  return {
    ...snapshot,
    elements: buildElements(),
    families: buildFamilies(),
  };
}

export const startE2eAwareQuickFillApply: QuickFillApply = async (
  familyTypeUpdates,
  elementUpdates,
  options
) => {
  if (!isMixedScopeFixtureActive(window.location.search)) {
    return startQuickFillApplyGrpc(familyTypeUpdates, elementUpdates, options);
  }

  window.__PIPEEX_E2E_QUICK_FILL_APPLY__ = structuredClone({
    familyTypeUpdates,
    elementUpdates,
  });

  return {
    success: true,
    familyTypeResult: {
      success: true,
      totalUpdates: familyTypeUpdates.length,
      successCount: familyTypeUpdates.length,
      errorCount: 0,
    },
    elementResult: {
      success: true,
      totalUpdates: elementUpdates.length,
      successCount: elementUpdates.length,
      errorCount: 0,
    },
  };
};
